import type { BorderStyleModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import { useOptimisticModifierValue } from "@v2/features/modifiers/hooks/useOptimisticModifierValue";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";

import BorderStyleSelector from "./BorderStyleSelector";

interface BorderStyleModifierProps {
  onChange: (modifier: BorderStyleModifierValue) => void;
}

const BorderStyleModifier: React.FC<BorderStyleModifierProps> = ({
  onChange,
}) => {
  const extractedStyle =
    useOptimisticModifierValue({
      modifierType: { type: "borderStyle", value: "solid" },
      extractor: (elementDesignation) => {
        return (
          elementDesignation.element.styleOverrides?.borderTopStyle ?? "none"
        );
      },
    }) ?? "none";

  const [currentStyle, setCurrentStyle] = useOverridableState(
    extractedStyle,
    React.useCallback(
      (style: string | null) => {
        const modifier: BorderStyleModifierValue = {
          type: "borderStyle",
          value: style as "solid" | "dashed" | "dotted" | "double",
        };
        onChange(modifier);
      },
      [onChange],
    ),
  );

  const handleStyleChange = React.useCallback(
    (style: string | null) => {
      setCurrentStyle(style ?? "none");
    },
    [setCurrentStyle],
  );

  return (
    <BorderStyleSelector
      borderStyle={currentStyle}
      onSelect={handleStyleChange}
    />
  );
};

export default BorderStyleModifier;
